/**
 * Derivación de la paleta a partir del matiz de marca (`brand.accentHue`).
 *
 * El design system "Jade y Maíz" tiene un acento de marca para las acciones
 * (el maíz de resaltes es fijo). La institución elige su matiz (0–360) y aquí
 * se derivan los tokens HSL de claro y oscuro: acento, anillo de foco, tinta,
 * menú lateral oscuro y superficies oscuras teñidas del mismo matiz. La luminosidad del acento se ajusta hasta cumplir contraste WCAG AA
 * (4.5:1) con su texto, sea cual sea el matiz (los amarillos exigen más).
 *
 * Archivo puro (sin DOM ni alias `@/`): también lo usa `vite.config.ts`
 * para generar el manifiesto PWA.
 */

/** Matiz por defecto (esmeralda de Tumin), igual a `defaultOrganizationSettings().brand.accentHue`. */
export const DEFAULT_BRAND_HUE = 158

/** Contraste mínimo WCAG AA para texto normal. */
export const MIN_TEXT_CONTRAST = 4.5

/** Tokens que se derivan del matiz (nombres de variables CSS sin `--`). */
export const BRAND_TOKENS = [
  'foreground',
  'card-foreground',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary-foreground',
  'accent-foreground',
  'ring',
  'background',
  'card',
  'popover',
  'secondary',
  'muted',
  'muted-foreground',
  'accent',
  'border',
  'input',
  'shadow-color',
  'sidebar',
  'sidebar-foreground',
  'sidebar-muted',
  'sidebar-accent',
  'sidebar-border',
] as const
export type BrandToken = (typeof BRAND_TOKENS)[number]

/** Paleta completa: valores HSL "H S% L%" por token, para claro y oscuro. */
export interface BrandPalette {
  light: Record<BrandToken, string>
  dark: Record<BrandToken, string>
}

interface Hsl {
  h: number
  s: number
  l: number
}

const FULL_CIRCLE = 360
const PERCENT = 100

/** Normaliza cualquier número a un matiz entero 0–359. */
export function normalizeHue(hue: number): number {
  if (!Number.isFinite(hue)) return DEFAULT_BRAND_HUE
  return ((Math.round(hue) % FULL_CIRCLE) + FULL_CIRCLE) % FULL_CIRCLE
}

/** HSL → RGB (0–255). */
export function hslToRgb({ h, s, l }: Hsl): [number, number, number] {
  const sat = s / PERCENT
  const light = l / PERCENT
  const k = (n: number) => (n + h / 30) % 12
  const a = sat * Math.min(light, 1 - light)
  const f = (n: number) => light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  const CHANNEL_MAX = 255
  return [f(0), f(8), f(4)].map((v) => Math.round(v * CHANNEL_MAX)) as [number, number, number]
}

/** Luminancia relativa WCAG de un color RGB. */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Relación de contraste WCAG entre dos colores HSL. */
export function contrastRatio(a: Hsl, b: Hsl): number {
  const la = relativeLuminance(hslToRgb(a))
  const lb = relativeLuminance(hslToRgb(b))
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

function toToken({ h, s, l }: Hsl): string {
  return `${String(h)} ${String(s)}% ${String(l)}%`
}

/** Hex `#rrggbb` de un color HSL (para `theme-color` y el manifiesto). */
export function hslToHex(color: Hsl): string {
  return `#${hslToRgb(color)
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`
}

/** Oscurece (paso −1) o aclara (+1) el color hasta alcanzar el contraste pedido contra `against`. */
function adjustForContrast(color: Hsl, against: Hsl, step: 1 | -1, minRatio = MIN_TEXT_CONTRAST): Hsl {
  let current = color
  while (contrastRatio(current, against) < minRatio && current.l > 0 && current.l < PERCENT) {
    current = { ...current, l: current.l + step }
  }
  return current
}

/** Papel cálido del fondo en modo claro (#F6F2E9). */
const LIGHT_PAPER = '42 42% 94%'

/** Texto claro sobre el acento en modo claro (hueso casi blanco). */
const LIGHT_ON_PRIMARY: Hsl = { h: 45, s: 40, l: 98 }
/** Saturación y luminosidad de partida del acento en modo claro. */
const LIGHT_PRIMARY_START = { s: 78, l: 27 }
/** Saturación y luminosidad de partida del acento en modo oscuro. */
const DARK_PRIMARY_START = { s: 58, l: 46 }
/** Aclarado del anillo de foco respecto del acento. */
const RING_LIGHTEN = 3

/** Fondo del menú lateral en modo claro: tinta profunda del matiz (#0D221C con el jade). */
const LIGHT_SIDEBAR = { s: 45, l: 9 }
/** Fondo del menú lateral en modo oscuro: apenas distinto del fondo de la página. */
const DARK_SIDEBAR = { s: 16, l: 7 }
/** Texto principal del menú: casi blanco con un toque del matiz. */
const SIDEBAR_TEXT = { s: 15, l: 88 }
/** Texto secundario del menú (rótulos de grupo); se aclara hasta cumplir AA. */
const SIDEBAR_MUTED_START = { s: 14, l: 61 }

/** Tokens del menú lateral oscuro para un fondo dado. */
function sidebarTokens(h: number, base: { s: number; l: number }) {
  const background: Hsl = { h, ...base }
  return {
    sidebar: toToken(background),
    'sidebar-foreground': toToken(adjustForContrast({ h, ...SIDEBAR_TEXT }, background, 1)),
    'sidebar-muted': toToken(adjustForContrast({ h, ...SIDEBAR_MUTED_START }, background, 1)),
    'sidebar-accent': toToken({ h, s: base.s - 5, l: base.l + 5 }),
    'sidebar-border': toToken({ h, s: base.s - 14, l: base.l + 11 }),
  }
}

/** Acento del modo claro para un matiz, ya ajustado a contraste AA. */
export function lightPrimary(hue: number): Hsl {
  const h = normalizeHue(hue)
  return adjustForContrast({ h, ...LIGHT_PRIMARY_START }, LIGHT_ON_PRIMARY, -1)
}

/** Acento del modo oscuro para un matiz, ya ajustado a contraste AA con su texto oscuro. */
export function darkPrimary(hue: number): { primary: Hsl; onPrimary: Hsl } {
  const h = normalizeHue(hue)
  const onPrimary: Hsl = { h, s: 28, l: 6 }
  return { primary: adjustForContrast({ h, ...DARK_PRIMARY_START }, onPrimary, 1), onPrimary }
}

/** Deriva todos los tokens de marca de claro y oscuro a partir del matiz. */
export function derivePalette(hue: number): BrandPalette {
  const h = normalizeHue(hue)
  const primary = lightPrimary(h)
  const dark = darkPrimary(h)
  return {
    light: {
      foreground: toToken({ h, s: 28, l: 9 }),
      'card-foreground': toToken({ h, s: 28, l: 9 }),
      'popover-foreground': toToken({ h, s: 28, l: 9 }),
      primary: toToken(primary),
      'primary-foreground': toToken(LIGHT_ON_PRIMARY),
      'secondary-foreground': toToken({ h, s: 28, l: 14 }),
      'accent-foreground': toToken({ h, s: 28, l: 14 }),
      ring: toToken({ ...primary, l: primary.l + RING_LIGHTEN }),
      // Superficies claras: papel cálido, independiente del matiz.
      background: LIGHT_PAPER,
      card: '0 0% 100%',
      popover: '0 0% 100%',
      secondary: '40 32% 91%',
      muted: '40 32% 91%',
      'muted-foreground': toToken({ h, s: 10, l: 32 }),
      accent: '40 32% 91%',
      border: '42 27% 85%',
      input: '42 21% 76%',
      'shadow-color': toToken({ h, s: 24, l: 12 }),
      ...sidebarTokens(h, LIGHT_SIDEBAR),
    },
    dark: {
      foreground: '45 24% 93%',
      'card-foreground': '45 24% 93%',
      'popover-foreground': '45 24% 93%',
      primary: toToken(dark.primary),
      'primary-foreground': toToken(dark.onPrimary),
      'secondary-foreground': '45 24% 93%',
      'accent-foreground': '45 24% 93%',
      ring: toToken({ ...dark.primary, l: dark.primary.l + RING_LIGHTEN }),
      // Superficies oscuras teñidas del matiz de marca.
      background: toToken({ h, s: 16, l: 5 }),
      card: toToken({ h, s: 13, l: 8 }),
      popover: toToken({ h, s: 14, l: 7 }),
      secondary: toToken({ h, s: 10, l: 14 }),
      muted: toToken({ h, s: 10, l: 12 }),
      'muted-foreground': toToken({ h, s: 7, l: 64 }),
      accent: toToken({ h, s: 12, l: 15 }),
      border: toToken({ h, s: 10, l: 16 }),
      input: toToken({ h, s: 10, l: 18 }),
      'shadow-color': toToken({ h, s: 60, l: 2 }),
      ...sidebarTokens(h, DARK_SIDEBAR),
    },
  }
}

/** Serializa la paleta como CSS (`:root{…} :root.dark{…}`) lista para inyectar. */
export function paletteToCss(palette: BrandPalette): string {
  const block = (tokens: Record<BrandToken, string>) =>
    BRAND_TOKENS.map((token) => `--${token}:${tokens[token]};`).join('')
  return `:root{${block(palette.light)}}:root.dark{${block(palette.dark)}}`
}

/** Colores hex para el manifiesto PWA y `<meta name="theme-color">`. */
export function brandHexColors(hue: number): { primary: string; lightBackground: string; darkBackground: string } {
  const h = normalizeHue(hue)
  return {
    primary: hslToHex(lightPrimary(h)),
    lightBackground: hslToHex({ h: 42, s: 42, l: 94 }),
    darkBackground: hslToHex({ h, s: 16, l: 5 }),
  }
}
